/**
 * IDDLEditor — Iter-22 candidate-facing simulation primitive.
 *
 * Realistic message / email composer for the DS-CM Communication
 * concepts (Message Construction · Conduct). Designed around four
 * surfaces:
 *
 *   • Optional ORIGINAL-MESSAGE panel (renders when the contract
 *     supplies `original_message` — used for reply scenarios like
 *     DS-CM-04 where tone-matching matters).
 *   • COMPOSER — To / Cc / Subject / Body, all editable.
 *   • TONE-SIGNAL sidebar — real-time check of the body against the
 *     contract's `tone_signals` array. Every signal is a
 *     deterministic substring / regex / all-caps detector with
 *     a `must_be_absent` truth flag. Hit signals raise an inline
 *     warning and the candidate can EDIT them out OR acknowledge.
 *   • SEND button — final_decision.
 *
 * Per the iter-22 directive there is NO LLM-based tone analysis —
 * every signal is author-defined and deterministic. Multi-object
 * scoring keys on `signal_resolved` per `signal_id` (emitted at
 * submit for each signal whose final body satisfies its truth
 * flag), mirroring the iddl_settings pattern.
 *
 * Canonical events (~12):
 *   editor_opened
 *   recipient_changed
 *   subject_changed
 *   body_changed          (debounced)
 *   tone_signal_triggered
 *   tone_signal_cleared
 *   warning_acknowledged
 *   draft_revised         (counts substantive revisions)
 *   signal_resolved       (per signal at submit when satisfied)
 *   send_pressed
 *   final_decision
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Mail, AlertTriangle, Send, CheckCircle2, RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";

/* ─── Deterministic tone-signal evaluation ───────────────────────────
 * Each signal has a kind and a parameter. The contract author
 * controls everything — no inference, no LLM.
 *   • banned_substring   — fires when body contains `value` (case-insensitive)
 *   • required_substring — fires when body does NOT contain `value`
 *   • all_caps_run       — fires when body contains N+ consecutive A-Z
 *   • regex              — fires when body matches the (case-insensitive) regex
 * Returns true when the signal is FIRED (i.e. unsafe).
 */
function evaluateSignal(signal, body) {
  const s = String(body || "");
  const kind = signal.kind;
  if (kind === "banned_substring") {
    return s.toLowerCase().includes(String(signal.value).toLowerCase());
  }
  if (kind === "required_substring") {
    return !s.toLowerCase().includes(String(signal.value).toLowerCase());
  }
  if (kind === "all_caps_run") {
    const n = signal.min_run || 5;
    const re = new RegExp(`[A-Z]{${n},}`);
    return re.test(s);
  }
  if (kind === "regex") {
    // Semantics: the regex describes a REQUIRED pattern. The signal
    // FIRES when the pattern is NOT matched (i.e. the unsafe state).
    // Pair with `must_be_absent: true` so satisfied = pattern present.
    try {
      const re = new RegExp(signal.value, signal.flags || "i");
      return !re.test(s);
    } catch (e) {
      return false;
    }
  }
  return false;
}

export default function IDDLEditor({
  attemptId,
  conceptId,
  scenarioText,
  onFinalize,
  onEventLogged,
}) {
  const { t } = useT();
  const original = scenarioText?.original_message || null;
  const initialDraft = scenarioText?.initial_draft || {
    to: "", cc: "", subject: "", body: "",
  };
  const toneSignals = scenarioText?.tone_signals || [];

  const [to, setTo] = useState(initialDraft.to || "");
  const [cc, setCc] = useState(initialDraft.cc || "");
  const [subject, setSubject] = useState(initialDraft.subject || "");
  const [body, setBody] = useState(initialDraft.body || "");
  const [acknowledged, setAcknowledged] = useState({}); // {signal_id: true}
  const [finalized, setFinalized] = useState(null);
  const lastBodyRef = useRef(initialDraft.body || "");
  const lastFiredRef = useRef({}); // {signal_id: boolean}
  const openedRef = useRef(false);
  const revisionCountRef = useRef(0);

  async function emit(eventName, eventValue, screenContext) {
    try {
      const r = await api
        .post(`/attempts/${attemptId}/events`, {
          concept_id: conceptId,
          event_name: eventName,
          event_value: eventValue,
          screen_context: screenContext,
          client_ts_ms: Date.now(),
        })
        .then((x) => x.data);
      if (onEventLogged && r?.event) onEventLogged(r.event);
      return r?.event;
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Event failed";
      toast.error(`Event ${eventName} failed: ${detail}`);
      return null;
    }
  }

  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    emit("editor_opened", {
      tone_signal_count: toneSignals.length,
      has_original: !!original,
    }, "editor");
    // Seed the lastFiredRef with the initial-draft signal state so we
    // emit accurate triggered/cleared transitions.
    for (const sig of toneSignals) {
      lastFiredRef.current[sig.id] = evaluateSignal(sig, initialDraft.body || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compute live signal state
  const signalState = useMemo(() => {
    const out = {};
    for (const sig of toneSignals) {
      const fired = evaluateSignal(sig, body);
      // A signal is "OK" when `must_be_absent` (true) AND not fired,
      // OR when `must_be_absent` is false (signal IS expected) AND
      // fired. We treat default truth as `must_be_absent=true`.
      const mustBeAbsent = sig.must_be_absent !== false;
      const satisfied = mustBeAbsent ? !fired : fired;
      out[sig.id] = { fired, satisfied };
    }
    return out;
  }, [toneSignals, body]);

  const unsatisfiedSignals = useMemo(
    () => toneSignals.filter((s) => !signalState[s.id]?.satisfied),
    [toneSignals, signalState],
  );

  // Detect signal transitions on body change and emit corresponding events.
  const updateBody = (next) => {
    if (finalized) return;
    if (next === body) return;
    // Count substantive revisions (≥3-char delta) as draft_revised.
    if (Math.abs((next || "").length - (lastBodyRef.current || "").length) >= 3) {
      revisionCountRef.current += 1;
      emit("draft_revised", {
        revision_index: revisionCountRef.current,
        body_len: (next || "").length,
      }, "editor");
      lastBodyRef.current = next;
    }
    setBody(next);
    emit("body_changed", {
      body_len: (next || "").length,
    }, "editor");
    // Compute signal transitions
    for (const sig of toneSignals) {
      const fired = evaluateSignal(sig, next);
      const prev = lastFiredRef.current[sig.id];
      if (fired && !prev) {
        emit("tone_signal_triggered", {
          signal_id: sig.id, label: sig.label, kind: sig.kind,
        }, "editor");
        setAcknowledged((a) => ({ ...a, [sig.id]: false }));
      } else if (!fired && prev) {
        emit("tone_signal_cleared", {
          signal_id: sig.id, label: sig.label,
        }, "editor");
      }
      lastFiredRef.current[sig.id] = fired;
    }
  };

  const onAck = (sig) => {
    emit("warning_acknowledged", {
      signal_id: sig.id, label: sig.label,
    }, "editor");
    setAcknowledged((a) => ({ ...a, [sig.id]: true }));
  };

  const submitFinal = () => {
    if (finalized) return;
    // Emit signal_resolved per satisfied signal (multi-object scorer
    // keys on this) at submit time.
    for (const sig of toneSignals) {
      if (signalState[sig.id]?.satisfied) {
        emit("signal_resolved", {
          signal_id: sig.id, message_id: sig.id, label: sig.label,
        }, "editor");
      }
    }
    emit("send_pressed", {
      to, cc, subject,
      body_len: (body || "").length,
      unsatisfied_count: unsatisfiedSignals.length,
    }, "editor");
    emit("final_decision", {
      decision: "sent",
      to, cc, subject, body,
    }, "editor");
    setFinalized("sent");
    onFinalize?.({ decision: "sent" });
  };

  return (
    <div
      className="border border-[var(--iddl-rule)] bg-white p-3 space-y-3"
      data-testid="iddl-editor"
    >
      {/* Optional original message panel */}
      {original && (
        <section
          className="border border-[var(--iddl-rule)] bg-[var(--iddl-paper)] p-3"
          data-testid="iddl-ed-original"
        >
          <div className="label-mono mb-2 flex items-center gap-2">
            <Mail size={12} />
            {t("sim.ed.original_title")}
          </div>
          <div className="text-xs">
            <div><strong>{t("sim.ed.from")}:</strong> {original.from}</div>
            {original.to && <div><strong>{t("sim.ed.to")}:</strong> {original.to}</div>}
            {original.subject && <div><strong>{t("sim.ed.subject")}:</strong> {original.subject}</div>}
            <div className="mt-2 whitespace-pre-wrap text-[var(--iddl-muted)]">
              {original.body}
            </div>
          </div>
        </section>
      )}

      <div className="grid lg:grid-cols-3 gap-3">
        {/* Composer */}
        <section
          className="lg:col-span-2 border border-[var(--iddl-rule)] p-3"
          data-testid="iddl-ed-composer"
        >
          <div className="label-mono mb-2 flex items-center gap-2">
            <Mail size={12} />
            {t("sim.ed.composer_title")}
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <label className="w-16 text-[var(--iddl-muted)]">{t("sim.ed.to")}</label>
              <input
                value={to}
                onChange={(e) => {
                  if (finalized) return;
                  setTo(e.target.value);
                  emit("recipient_changed", { field: "to", value: e.target.value }, "editor");
                }}
                disabled={!!finalized}
                className="flex-1 border border-[var(--iddl-rule)] px-2 py-1"
                data-testid="iddl-ed-to"
              />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <label className="w-16 text-[var(--iddl-muted)]">{t("sim.ed.cc")}</label>
              <input
                value={cc}
                onChange={(e) => {
                  if (finalized) return;
                  setCc(e.target.value);
                  emit("recipient_changed", { field: "cc", value: e.target.value }, "editor");
                }}
                disabled={!!finalized}
                className="flex-1 border border-[var(--iddl-rule)] px-2 py-1"
                data-testid="iddl-ed-cc"
              />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <label className="w-16 text-[var(--iddl-muted)]">{t("sim.ed.subject")}</label>
              <input
                value={subject}
                onChange={(e) => {
                  if (finalized) return;
                  setSubject(e.target.value);
                  emit("subject_changed", { value: e.target.value }, "editor");
                }}
                disabled={!!finalized}
                className="flex-1 border border-[var(--iddl-rule)] px-2 py-1"
                data-testid="iddl-ed-subject"
              />
            </div>
            <textarea
              value={body}
              onChange={(e) => updateBody(e.target.value)}
              disabled={!!finalized}
              rows={12}
              className="w-full border border-[var(--iddl-rule)] p-2 text-sm font-mono"
              placeholder={t("sim.ed.body_placeholder")}
              data-testid="iddl-ed-body"
            />
          </div>
        </section>

        {/* Tone-signal sidebar */}
        <aside
          className="border border-[var(--iddl-rule)] p-3"
          data-testid="iddl-ed-signals"
        >
          <div className="label-mono mb-2 flex items-center gap-2">
            <AlertTriangle size={12} />
            {t("sim.ed.signals_title")}
          </div>
          {toneSignals.length === 0 && (
            <div className="text-xs text-[var(--iddl-muted)] italic">
              {t("sim.ed.signals_none")}
            </div>
          )}
          <ul className="space-y-2">
            {toneSignals.map((sig) => {
              const st = signalState[sig.id] || {};
              const isUnsat = !st.satisfied;
              const ack = !!acknowledged[sig.id];
              return (
                <li
                  key={sig.id}
                  className={`border p-2 text-xs ${
                    isUnsat
                      ? "border-rose-400 bg-rose-50/40"
                      : "border-emerald-300 bg-emerald-50"
                  }`}
                  data-testid={`iddl-ed-signal-${sig.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {isUnsat
                      ? <AlertTriangle size={12} className="text-rose-700" />
                      : <CheckCircle2 size={12} className="text-emerald-700" />}
                    <span className="font-semibold flex-1">{sig.label}</span>
                  </div>
                  {isUnsat && !ack && (
                    <>
                      <div className="text-[var(--iddl-muted)] mb-2">
                        {sig.warning_text || t("sim.ed.warning_default")}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => onAck(sig)}
                        className="rounded-none btn-ink"
                        data-testid={`iddl-ed-signal-ack-${sig.id}`}
                      >
                        {t("sim.ed.warning_ack")}
                      </Button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </aside>
      </div>

      {/* Footer · send */}
      <div
        className="border-t border-[var(--iddl-rule)] pt-3 flex items-center gap-3"
        data-testid="iddl-ed-final"
      >
        <div className="text-sm flex-1">
          {unsatisfiedSignals.length > 0 ? (
            <span className="text-rose-700 flex items-center gap-1.5">
              <AlertTriangle size={12} />
              {t("sim.ed.unsatisfied_count", { n: unsatisfiedSignals.length })}
            </span>
          ) : (
            <span className="text-emerald-700 flex items-center gap-1.5">
              <CheckCircle2 size={12} />
              {t("sim.ed.draft_ready")}
            </span>
          )}
        </div>
        <Button
          onClick={submitFinal}
          disabled={!!finalized}
          className="rounded-none btn-ink"
          data-testid="iddl-ed-btn-send"
        >
          <Send size={12} className="mr-2" />
          {t("sim.ed.btn_send")}
        </Button>
      </div>
      {finalized && (
        <div className="text-xs text-[var(--iddl-muted)]" data-testid="iddl-ed-finalized">
          {t("sim.ed.finalized", { decision: finalized })}
        </div>
      )}
    </div>
  );
}
